import type { CampaignDetailCampaign } from "@adpilot/shared";
import { IconExternalLink } from "@tabler/icons-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { BudgetHealthBadge } from "@/components/shared/BudgetHealthBadge";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useFormatters } from "@/lib/format";
import { CampaignEditDrawer } from "../campaign-edit-drawer";
import { CampaignBudgetHistoryCard } from "./CampaignBudgetHistoryCard";
import { CampaignBudgetRulesCard } from "./CampaignBudgetRulesCard";

const LINEAR_ISSUE_URL = "https://linear.app/adpilot/issue";

/**
 * ADP-220: reference-page budget capabilities that have no local data track.
 * Each stays visibly disabled with its reason and follow-up issue instead of
 * rendering fabricated numbers (see the issue's capability audit).
 */
const ADVANCED_CAPABILITIES = [{ key: "usageStats", issue: "ADP-123" }] as const;

interface CampaignBudgetPanelProps {
  campaign: CampaignDetailCampaign;
  canWrite: boolean;
}

export function CampaignBudgetPanel({ campaign, canWrite }: CampaignBudgetPanelProps) {
  const { t } = useTranslation("campaign");
  const { formatCurrency, formatPercent, formatDateTime } = useFormatters();
  const health = campaign.budgetHealth;
  const currency = health.currency ?? "USD";
  const money = (value: number) => formatCurrency(value, currency);
  // `budgetType` is null when Amazon did not report one; the header treats that
  // as daily, so the usage block does too — only an explicit monthly cap opts out.
  const monthly = campaign.budgetType === "monthly";
  const budget = campaign.dailyBudget;
  // Read `budgetHealth.spend`, not `todaySpend`: the latter flattens "no report
  // row yet" to a compat 0, which must not read as evidence of zero spend.
  const spend = health.spend;
  // Same gate as the server's utilization so a zero budget or a currency
  // mismatch never yields a fabricated "remaining" next to a `unknown` badge.
  const remaining =
    health.utilization != null && health.budget != null && spend != null
      ? Math.max(health.budget - spend, 0)
      : null;

  const usageWindow =
    health.asOfDate == null
      ? null
      : health.timeZone
        ? t("detail.budget.usage.window", { date: health.asOfDate, timeZone: health.timeZone })
        : t("detail.budget.usage.windowUtc", { date: health.asOfDate });

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader>
          <CardTitle>{t("detail.budget.usage.title")}</CardTitle>
          <CardDescription>{t("detail.budget.usage.estimate")}</CardDescription>
          <CardAction>
            <BudgetHealthBadge health={health} />
          </CardAction>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-4 divide-x divide-line rounded-[10px] border border-line">
            <Stat
              label={t("detail.budget.usage.budget")}
              value={
                budget == null
                  ? t("detail.budget.settings.missing")
                  : monthly
                    ? `${money(budget)} / ${t("detail.shell.monthly")}`
                    : money(budget)
              }
              muted={budget == null}
            />
            <Stat
              label={t("detail.budget.usage.spend")}
              value={spend == null ? t("detail.budget.usage.noReport") : money(spend)}
              muted={spend == null}
            />
            <Stat
              label={t("detail.budget.usage.utilization")}
              value={
                monthly
                  ? t("detail.budget.usage.notDaily")
                  : health.utilization == null
                    ? "—"
                    : formatPercent(health.utilization)
              }
              muted={monthly || health.utilization == null}
            />
            <Stat
              label={t("detail.budget.usage.remaining")}
              value={
                monthly
                  ? t("detail.budget.usage.notDaily")
                  : remaining == null
                    ? "—"
                    : money(remaining)
              }
              muted={monthly || remaining == null}
            />
          </dl>
          <p className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[11px] leading-4 text-muted-foreground">
            {usageWindow ? <span>{usageWindow}</span> : null}
            {health.spendSyncedAt ? (
              <span>
                {t("detail.budget.usage.syncedAt", {
                  time: formatDateTime(health.spendSyncedAt),
                })}
              </span>
            ) : null}
          </p>
        </CardContent>
      </Card>

      <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] gap-4">
        <Card>
          <CardHeader>
            <CardTitle>{t("detail.budget.settings.title")}</CardTitle>
            <CardDescription>{t("detail.budget.settings.description")}</CardDescription>
            <CardAction>
              <CampaignEditDrawer campaign={campaign} canWrite={canWrite} />
            </CardAction>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 text-[13px] leading-5">
              <dt className="text-muted-foreground">{t("detail.budget.settings.type")}</dt>
              <dd className="font-medium">
                {campaign.budgetType == null
                  ? t("detail.budget.settings.unknownType")
                  : t(`detail.shell.${campaign.budgetType}`)}
              </dd>
              <dt className="text-muted-foreground">{t("detail.budget.settings.amount")}</dt>
              <dd className="font-medium tabular-nums">
                {budget == null ? t("detail.budget.settings.missing") : money(budget)}
              </dd>
              <dt className="text-muted-foreground">{t("detail.budget.settings.currency")}</dt>
              <dd className="font-medium">{currency}</dd>
            </dl>
            {monthly ? (
              <p className="mt-3 text-[11px] leading-4 text-muted-foreground">
                {t("detail.edit.monthlyBudget")}
              </p>
            ) : null}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t("detail.budget.advanced.title")}</CardTitle>
            <CardDescription>{t("detail.budget.advanced.description")}</CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="flex flex-col divide-y divide-line">
              {ADVANCED_CAPABILITIES.map((item) => (
                <li key={item.key} className="flex items-start gap-3 py-2.5 first:pt-0 last:pb-0">
                  <Badge variant="neutral" size="compact" className="mt-0.5 shrink-0">
                    {t("detail.budget.advanced.notConnected")}
                  </Badge>
                  <div className="flex min-w-0 flex-col gap-0.5 text-[12px] leading-4">
                    <span className="font-medium text-ink">
                      {t(`detail.budget.advanced.${item.key}.title`)}
                    </span>
                    <span className="text-muted-foreground">
                      {t(`detail.budget.advanced.${item.key}.reason`)}
                    </span>
                    <a
                      href={`${LINEAR_ISSUE_URL}/${item.issue}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex w-fit items-center gap-1 text-primary hover:underline focus-visible:outline-2 focus-visible:outline-ring"
                    >
                      {t("detail.budget.advanced.trackedIn", { issue: item.issue })}
                      <IconExternalLink className="size-3" aria-hidden />
                    </a>
                  </div>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </div>

      <CampaignBudgetRulesCard
        campaignId={campaign.id}
        campaignType={campaign.campaignType}
        archived={campaign.status === "archived"}
        dailyBudget={monthly ? null : budget}
        currency={currency}
        canWrite={canWrite}
      />

      <CampaignBudgetHistoryCard campaignId={campaign.id} campaignType={campaign.campaignType} />
    </div>
  );
}

function Stat({ label, value, muted }: { label: string; value: ReactNode; muted?: boolean }) {
  return (
    <div className="flex flex-col justify-center gap-1 px-4 py-3">
      <dt className="text-[9px] uppercase tracking-[0.04em] text-muted-foreground">{label}</dt>
      <dd
        className={
          muted
            ? "text-[13px] leading-5 text-muted-foreground"
            : "text-[20px] font-semibold leading-6 tabular-nums text-ink"
        }
      >
        {value}
      </dd>
    </div>
  );
}
